import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Card, Container, Form, Modal, Spinner } from 'react-bootstrap';
import { getCompanyInfo } from '../../../../services/PublicBookingService';
import {
    getPlanOffers, getPlanSignupStatus, planContractUrl, requestPlanSignupCode,
    subscribeToPlan, verifyPlanSignupCode
} from '../../../../services/PublicPlanSignupService';
import { readSession, writeSession } from '../../../../services/PublicClubService';
import { getStoredCustomerDocument } from '../../../../utils/PublicBookingStorage';
import { formatIdentifier, isCompleteIdentifier, toIdentifierParam } from '../../../../utils/PublicBookingIdentifier';
import { WEEK_DAY_LONG_LABELS } from '../../../../utils/WorkDayUtils';
import ClientTabBar from '../shared/ClientTabBar';
import PublicHero from '../shared/PublicHero';
import PublicBrandSignature from '../../../components/Common/PublicBrandSignature';
import { formatBrl, getBrandVars } from '../shared/publicBrand';
import './PublicBooking.css';
import './PublicPlans.css';

const PERIODICITY_LABEL = {
    MONTHLY: 'por mês',
    BIMONTHLY: 'a cada 2 meses',
    QUARTERLY: 'a cada 3 meses',
    SEMI_ANNUAL: 'a cada 6 meses',
    YEARLY: 'por ano',
    BIENNIAL: 'a cada 2 anos'
};

const tokenKey = (company) => `plan-signup-token:${company}`;
const apiMessage = (error, fallback) => error?.response?.data?.message || fallback;

/**
 * Planos do ambiente do cliente: vitrine e contratação com cartão recorrente.
 *
 * O cartão nunca é digitado aqui. Depois do aceite do contrato o backend cria a assinatura e
 * devolve o link do provedor; o cliente é levado para lá para pagar. A assinatura só ativa
 * quando o provedor confirma o pagamento.
 */
const PublicPlansPage = () => {
    const { companyUuid } = useParams();

    const [companyInfo, setCompanyInfo] = useState(null);
    // null = ainda consultando; false = a empresa não oferece contratação (sem provedor).
    const [enabled, setEnabled] = useState(null);
    const [offers, setOffers] = useState([]);
    const [loadError, setLoadError] = useState('');

    const [selected, setSelected] = useState(null);
    const [token, setToken] = useState(() => readSession(tokenKey(companyUuid)));
    const [step, setStep] = useState('identify');
    const [identifier, setIdentifier] = useState(() => getStoredCustomerDocument(companyUuid) || '');
    const [code, setCode] = useState('');
    const [maskedEmail, setMaskedEmail] = useState('');
    const [accepted, setAccepted] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [redirecting, setRedirecting] = useState('');
    const [activeOffer, setActiveOffer] = useState(0);
    const offersRef = useRef(null);

    useEffect(() => {
        getCompanyInfo(companyUuid).then(({ data }) => setCompanyInfo(data)).catch(() => setCompanyInfo(null));
        getPlanSignupStatus(companyUuid)
            .then(({ data }) => {
                setEnabled(Boolean(data.enabled));
                return data.enabled ? getPlanOffers(companyUuid) : null;
            })
            .then((response) => response && setOffers(response.data))
            .catch((err) => {
                setEnabled(false);
                setLoadError(apiMessage(err, 'Não foi possível carregar os planos agora.'));
            });
    }, [companyUuid]);

    const open = (offer) => {
        setSelected(offer);
        setError('');
        setCode('');
        setAccepted(false);
        // Sessão já confirmada neste aparelho pula direto para o contrato.
        setStep(token ? 'accept' : 'identify');
    };

    const close = () => {
        if (busy) return;
        setSelected(null);
    };

    const resetSession = () => {
        writeSession(tokenKey(companyUuid), '');
        setToken('');
        setStep('identify');
        setCode('');
    };

    const sendCode = async () => {
        if (!isCompleteIdentifier(identifier)) {
            setError('Informe um CPF ou e-mail válido.');
            return;
        }
        try {
            setBusy(true);
            setError('');
            const { data } = await requestPlanSignupCode(companyUuid, toIdentifierParam(identifier));
            setMaskedEmail(data.maskedEmail);
            setStep('code');
        } catch (err) {
            setError(apiMessage(err, 'Não foi possível enviar o código agora.'));
        } finally {
            setBusy(false);
        }
    };

    const confirmCode = async () => {
        try {
            setBusy(true);
            setError('');
            const { data } = await verifyPlanSignupCode(companyUuid, toIdentifierParam(identifier), code);
            writeSession(tokenKey(companyUuid), data.token);
            setToken(data.token);
            setStep('accept');
        } catch (err) {
            setError(apiMessage(err, 'Código inválido ou vencido.'));
        } finally {
            setBusy(false);
        }
    };

    const subscribe = async () => {
        try {
            setBusy(true);
            setError('');
            const { data } = await subscribeToPlan(companyUuid, token, selected.uuid, selected.contractReference);
            setRedirecting(data.paymentLink);
            // Mesma aba: pop-up para a página de pagamento costuma ser bloqueado no celular.
            window.location.assign(data.paymentLink);
        } catch (err) {
            if (err?.response?.status === 401) {
                resetSession();
                setError('Sua confirmação venceu. Confirme seu e-mail de novo.');
            } else {
                setError(apiMessage(err, 'Não foi possível contratar agora. Tente novamente.'));
            }
        } finally {
            setBusy(false);
        }
    };

    const dayLabels = (days) => (days || []).map((day) => WEEK_DAY_LONG_LABELS[day] || day).join(', ');

    // A navegação usa o mesmo carrossel do gesto de deslizar, sem trocar o conteúdo dos cartões.
    const goToOffer = (index) => {
        const list = offersRef.current;
        const card = list?.children[index];
        if (!card) return;
        list.scrollTo({ left: list.scrollLeft + card.getBoundingClientRect().left - list.getBoundingClientRect().left, behavior: 'smooth' });
    };

    const updateActiveOffer = () => {
        const list = offersRef.current;
        if (!list) return;
        const left = list.getBoundingClientRect().left;
        const index = Array.from(list.children).reduce((closest, card, current, cards) => (
            Math.abs(card.getBoundingClientRect().left - left) < Math.abs(cards[closest].getBoundingClientRect().left - left) ? current : closest
        ), 0);
        setActiveOffer(index);
    };

    return (
        <div className="public-booking-page public-plans-page" style={getBrandVars(companyInfo)}>
            <div className="public-plans-page__scroll">
            <PublicHero companyInfo={companyInfo} title="Planos" subtitle="Contrate um plano e economize em cada atendimento." />

            <Container className="booking-container booking-container--client-tab pb-4">
                <div className="booking-workspace">
                    <div className="booking-toolbar plan-intro">
                        <div className="plan-intro__copy">
                            <span className="booking-toolbar-eyebrow"><i className="fa-solid fa-crown" /> Assinatura</span>
                            <h2 className="booking-online-title">Encontre o plano ideal</h2>
                            <p className="text-muted mb-0">Escolha seus benefícios e aproveite cada atendimento. Pagamento recorrente no cartão.</p>
                        </div>
                        <Link to={`/booking/${companyUuid}`} state={{ startBookingFresh: true }} className="booking-toolbar__back plan-intro__back">
                            <i className="fa fa-arrow-left" aria-hidden="true" /><span>Voltar ao agendamento</span>
                        </Link>
                    </div>

                    {enabled === null && <div className="text-center p-4"><Spinner animation="border" /></div>}

                    {enabled === false && (
                        <Card className="booking-card shadow-lg text-center">
                            <Card.Body className="py-5">
                                <i className="fa-solid fa-crown fa-2x text-muted mb-3"></i>
                                <h5 className="text-muted">{loadError || 'A contratação de planos não está disponível no momento.'}</h5>
                            </Card.Body>
                        </Card>
                    )}

                    {enabled && offers.length > 0 && (
                        <>
                        {offers.length > 1 && <p className="plan-offers-swipe-hint"><i className="fa-solid fa-arrows-left-right" aria-hidden="true" /> Deslize para comparar os planos</p>}
                        <div className={`plan-offers-list${offers.length === 1 ? ' plan-offers-list--single' : ''}`} ref={offersRef} onScroll={updateActiveOffer}>
                            {offers.map((offer) => {
                                const includedBenefits = offer.benefits.filter((benefit) => benefit.discountPercentage >= 100);
                                const otherBenefits = offer.benefits.filter((benefit) => benefit.discountPercentage < 100);
                                return (
                                <Card key={offer.uuid} className="booking-card plan-offer">
                                    <Card.Body>
                                        <div className="plan-offer__main">
                                            <div className="plan-offer__heading">
                                                <span className="plan-offer__eyebrow">Plano</span>
                                                <h3>{offer.name}</h3>
                                            </div>
                                            <div className="plan-offer__price">
                                                <strong>{formatBrl(offer.price)}</strong>
                                                <span>{PERIODICITY_LABEL[offer.periodicity] || ''}</span>
                                            </div>
                                        </div>

                                        <div className="plan-offer__validity">
                                            <i className="fa-regular fa-calendar" />
                                            <span>{offer.benefitDays.length > 0 && offer.benefitDays.length < 7
                                                ? `Válido ${dayLabels(offer.benefitDays)}`
                                                : 'Válido todos os dias'}</span>
                                        </div>

                                        {includedBenefits.length > 0 && (
                                            <div className="plan-offer__included">
                                                <span className="plan-offer__included-label"><i className="fa-solid fa-star" /> 100% incluído</span>
                                                <div className="plan-offer__included-list">
                                                    {includedBenefits.map((benefit) => <span key={benefit.name} className="plan-offer__included-item"><i className="fa-solid fa-circle-check" />{benefit.name}</span>)}
                                                </div>
                                            </div>
                                        )}
                                        {includedBenefits.length === 0 && (
                                            <div className="plan-offer__included plan-offer__included--empty">
                                                <span className="plan-offer__included-label"><i className="fa-solid fa-star" /> 100% incluído</span>
                                                <span className="plan-offer__empty-hint">Nenhum item incluso neste plano</span>
                                            </div>
                                        )}

                                        {otherBenefits.length > 0 && (
                                            <details className="plan-offer__details">
                                                <summary>Ver outros {otherBenefits.length === 1 ? 'benefício' : `${otherBenefits.length} benefícios`}</summary>
                                                <ul className="plan-offer__benefits">
                                                    {otherBenefits.map((benefit) => (
                                                        <li key={benefit.name}>
                                                            <i className="fa-solid fa-check" aria-hidden="true" />
                                                            <span>{benefit.name}</span>
                                                            <strong>{benefit.discountPercentage}% de desconto</strong>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </details>
                                        )}
                                        {otherBenefits.length === 0 && <div className="plan-offer__details plan-offer__details--empty">Sem outros descontos</div>}

                                        <div className="plan-offer__actions">
                                            <a href={planContractUrl(companyUuid, offer.uuid)} target="_blank" rel="noopener noreferrer" className="plan-offer__contract">
                                                <i className="fa-regular fa-file-lines" /> Ler contrato
                                            </a>
                                            <Button onClick={() => open(offer)}>Quero este plano <i className="fa-solid fa-arrow-right ms-2" /></Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                                );
                            })}
                        </div>
                        {offers.length > 1 && (
                            <nav className="plan-offers-navigation" aria-label="Navegação entre planos">
                                <button type="button" onClick={() => goToOffer(activeOffer - 1)} disabled={activeOffer === 0} aria-label="Plano anterior"><i className="fa-solid fa-chevron-left" aria-hidden="true" /></button>
                                <span className="plan-offers-navigation__count" aria-live="polite">{activeOffer + 1} de {offers.length}</span>
                                <button type="button" onClick={() => goToOffer(activeOffer + 1)} disabled={activeOffer === offers.length - 1} aria-label="Próximo plano"><i className="fa-solid fa-chevron-right" aria-hidden="true" /></button>
                            </nav>
                        )}
                        </>
                    )}

                    {enabled && offers.length === 0 && (
                        <Card className="booking-card shadow-lg text-center"><Card.Body className="py-5 text-muted">Nenhum plano disponível agora.</Card.Body></Card>
                    )}
                </div>
                <PublicBrandSignature className="is-compact" />
            </Container>
            </div>

            <Modal show={Boolean(selected)} onHide={close} centered className="public-plans-modal" style={getBrandVars(companyInfo)}>
                <Modal.Header closeButton={!busy}>
                    <Modal.Title>{selected ? `Contratar ${selected.name}` : ''}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    {step === 'identify' && (
                        <>
                            <p className="text-muted">Para sua segurança, enviamos um código para o e-mail do seu cadastro.</p>
                            <Form.Group>
                                <Form.Label>CPF ou e-mail do cadastro</Form.Label>
                                <Form.Control value={identifier} onChange={(event) => setIdentifier(formatIdentifier(event.target.value))}
                                    inputMode="text" autoComplete="off" disabled={busy} />
                            </Form.Group>
                        </>
                    )}

                    {step === 'code' && (
                        <>
                            <p className="text-muted">Enviamos um código para <strong>{maskedEmail}</strong>. Ele vale por 10 minutos.</p>
                            <Form.Group>
                                <Form.Label>Código</Form.Label>
                                <Form.Control value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                                    inputMode="numeric" autoComplete="one-time-code" disabled={busy} />
                            </Form.Group>
                            <Button variant="link" className="px-0" onClick={sendCode} disabled={busy}>Enviar um novo código</Button>
                        </>
                    )}

                    {step === 'accept' && selected && (
                        <>
                            <p>
                                {formatBrl(selected.price)} {PERIODICITY_LABEL[selected.periodicity] || ''}, cobrados no cartão e renovados automaticamente.
                                O pagamento é feito na página segura do provedor, e o plano só vale depois que ele confirmar.
                            </p>
                            <a className="btn btn-outline-secondary btn-sm mb-3" href={planContractUrl(companyUuid, selected.uuid)} target="_blank" rel="noopener noreferrer">
                                <i className="fa-solid fa-file-contract me-2"></i>Ler o contrato
                            </a>
                            <Form.Check id="plan-accept" type="checkbox" checked={accepted} disabled={busy || Boolean(redirecting)}
                                onChange={(event) => setAccepted(event.target.checked)}
                                label="Li e aceito o contrato do plano." />
                            <Button variant="link" className="px-0 mt-2" onClick={resetSession} disabled={busy}>Não sou eu / trocar cliente</Button>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={close} disabled={busy}>Cancelar</Button>
                    {step === 'identify' && <Button onClick={sendCode} disabled={busy}>{busy ? 'Enviando...' : 'Enviar código'}</Button>}
                    {step === 'code' && <Button onClick={confirmCode} disabled={busy || code.length < 6}>{busy ? 'Conferindo...' : 'Confirmar'}</Button>}
                    {step === 'accept' && (
                        <Button onClick={subscribe} disabled={busy || !accepted || Boolean(redirecting)}>
                            {redirecting ? 'Abrindo o pagamento...' : busy ? 'Contratando...' : 'Contratar e pagar'}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            <ClientTabBar companyUuid={companyUuid} />
        </div>
    );
};

export default PublicPlansPage;
